import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { createAsyncThunk } from '@/store/create-async-thunk';

import { clientAutoCreditApi } from '../api';
import {
  formatBankResponse,
  formatLeasingResponse
} from '../shared/services/helpers/formatBankResponse';
import type { IBankOffer, ILeasingOffer } from './Offers/types/response';
import { ERROR_RESPONSE_STATUSES } from './shared/constants/auto-credit';
import type { IClientAutoCreditStore } from './types/store.types';

const initialState: IClientAutoCreditStore = {
  requests: {
    clientRequests: [],
    primaryRequests: [],
    archivedRequests: []
  },
  autoCreditRequest: null,

  bankResponses: [],
  leasingResponses: [],

  approvedOffers: [],
  approvedLeasingOffers: [],

  waitingOffers: [],
  waitingLeasingOffers: [],

  declinedOffers: [],
  declinedLeasingOffers: [],

  requestToContinue: '',
  currentStepFinancing: 1,

  isResponsesLoading: true,
  isResponsesRefreshing: false,
  responsesRefreshToken: 0
};

export const getAutoCreditRequest = createAsyncThunk(
  'client/auto-credit/getAutoCreditRequest',
  async ({ uuid }: { uuid: string }) =>
    clientAutoCreditApi.getAutoCreditRequest(uuid)
);

export const getBankResponses = createAsyncThunk(
  'client/auto-credit/getBankResponses',
  async ({ uuid }: { uuid: string }) => clientAutoCreditApi.getResponses(uuid)
);

const clientAutoCreditSlice = createSlice({
  name: 'client/auto-credit',
  initialState,
  reducers: {
    setRequests: (
      state,
      { payload }: PayloadAction<IClientAutoCreditStore['requests']>
    ) => {
      state.requests = payload;
    },
    setRequestToContinue: (state, { payload }: PayloadAction<string>) => {
      state.requestToContinue = payload;
    },
    setCurrentStepFinancing: (state, { payload }: PayloadAction<number>) => {
      state.currentStepFinancing = payload;
    },
    bumpResponsesRefresh(state) {
      state.responsesRefreshToken += 1;
    },
    resetOnRequestPageLeave(state) {
      state.autoCreditRequest = initialState.autoCreditRequest;

      state.bankResponses = initialState.bankResponses;
      state.leasingResponses = initialState.leasingResponses;

      state.approvedOffers = initialState.approvedOffers;
      state.approvedLeasingOffers = initialState.approvedLeasingOffers;

      state.waitingOffers = initialState.waitingOffers;
      state.waitingLeasingOffers = initialState.waitingLeasingOffers;

      state.declinedOffers = initialState.declinedOffers;
      state.declinedLeasingOffers = initialState.declinedLeasingOffers;

      state.requestToContinue = initialState.requestToContinue;
      state.currentStepFinancing = initialState.currentStepFinancing;

      state.isResponsesLoading = initialState.isResponsesLoading;
      state.isResponsesRefreshing = initialState.isResponsesRefreshing;
      state.responsesRefreshToken = initialState.responsesRefreshToken;
    },
    setIsResponsesRefreshing: (state, { payload }: PayloadAction<boolean>) => {
      state.isResponsesRefreshing = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAutoCreditRequest.fulfilled, (state, { payload }) => {
        state.autoCreditRequest = payload;
      })
      .addCase(getBankResponses.pending, (state) => {
        state.isResponsesLoading = true;
      })
      .addCase(getBankResponses.rejected, (state) => {
        state.isResponsesLoading = false;
      })
      .addCase(getBankResponses.fulfilled, (state, { payload }) => {
        state.isResponsesLoading = false;

        const offers = formatBankResponse(
          payload.Responses,
          payload.RequestsProducts
        );
        const approvedOffers: IBankOffer[] = [],
          waitingOffers: IBankOffer[] = [],
          declinedOffers: IBankOffer[] = [];

        offers.forEach((offer) => {
          const statusCode = offer.status.code;
          if (
            statusCode === 'waiting_bank_response' ||
            statusCode === 'on_verification'
          ) {
            waitingOffers.push(offer);
          } else if (ERROR_RESPONSE_STATUSES.includes(statusCode)) {
            declinedOffers.push(offer);
          } else {
            approvedOffers.push(offer);
          }
        });

        state.bankResponses = offers;
        state.approvedOffers = approvedOffers;
        state.waitingOffers = waitingOffers;
        state.declinedOffers = declinedOffers;

        const leasingOffers = formatLeasingResponse(payload.Leasings[0]);
        const approvedLeasingOffers: ILeasingOffer[] = [],
          waitingLeasingOffers: ILeasingOffer[] = [],
          declinedLeasingOffers: ILeasingOffer[] = [];

        leasingOffers.forEach((offer) => {
          const statusCode = offer.status.code;
          if (
            statusCode === 'waiting_bank_response' ||
            statusCode === 'on_verification'
          ) {
            waitingLeasingOffers.push(offer);
          } else if (ERROR_RESPONSE_STATUSES.includes(statusCode)) {
            declinedLeasingOffers.push(offer);
          } else {
            approvedLeasingOffers.push(offer);
          }
        });

        state.leasingResponses = leasingOffers;
        state.approvedLeasingOffers = approvedLeasingOffers;
        state.waitingLeasingOffers = waitingLeasingOffers;
        state.declinedLeasingOffers = declinedLeasingOffers;
      });
  }
});

export const {
  setRequests,
  setRequestToContinue,
  bumpResponsesRefresh,
  resetOnRequestPageLeave,
  setIsResponsesRefreshing,
  setCurrentStepFinancing
} = clientAutoCreditSlice.actions;

export default clientAutoCreditSlice;
